import { ChangeDetectionStrategy, Component, inject, OnInit } from '@angular/core';
import {
  FormBuilder,
  FormControl,
  FormGroup,
  ReactiveFormsModule,
  Validators,
} from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatDialogModule, MatDialogRef } from '@angular/material/dialog';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { AuthenticationService } from '../../../core/authentication/authentication.service';
import { SecurityApiService } from '../../../core/http/security-api.service';
import { NotificationService } from '../../../core/services/notification.service';
import { CommonRegexPatterns } from '../../utilities/common-regex-patterns';
import { mustMatch } from '../../validators/must-match.validator';
import { ChangePassword } from './change-password.model';

/**
 * Typed form interface for password change dialog.
 */
interface PasswordForm {
  userId: FormControl<number>;
  password: FormControl<string | null>;
  newPassword: FormControl<string | null>;
  newPassword2: FormControl<string | null>;
}

/**
 * Dialog component for changing user password.
 * Validates password requirements and confirms new password matches.
 */
@Component({
  selector: 'app-change-password',
  templateUrl: './change-password.component.html',
  styleUrl: './change-password.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    ReactiveFormsModule,
    MatButtonModule,
    MatDialogModule,
    MatFormFieldModule,
    MatInputModule,
  ],
})
export class ChangePasswordComponent implements OnInit {
  private readonly fb = inject(FormBuilder);
  private readonly dialogRef = inject(MatDialogRef<ChangePasswordComponent>);
  private readonly authService = inject(AuthenticationService);
  private readonly securityApi = inject(SecurityApiService);
  private readonly notificationService = inject(NotificationService);

  passwordForm!: FormGroup<PasswordForm>;

  ngOnInit(): void {
    this.initializeForm();
  }

  private initializeForm(): void {
    const currentUser = this.authService.cloneCurrentUser();
    const userId = currentUser?.appUserId ?? 0;

    this.passwordForm = this.fb.group<PasswordForm>(
      {
        userId: this.fb.control(userId, { nonNullable: true }),
        password: this.fb.control(null, Validators.required),
        newPassword: this.fb.control(null, [
          Validators.required,
          Validators.pattern(CommonRegexPatterns.PASSWORD),
        ]),
        newPassword2: this.fb.control(null, [
          Validators.required,
          Validators.pattern(CommonRegexPatterns.PASSWORD),
        ]),
      },
      {
        validators: [mustMatch('newPassword', 'newPassword2')],
      },
    );
  }

  changePassword(): void {
    const formValue = this.passwordForm.getRawValue();
    const payload: ChangePassword = {
      userId: formValue.userId,
      password: formValue.password ?? '',
      newPassword: formValue.newPassword ?? '',
      newPassword2: formValue.newPassword2 ?? '',
    };
    this.securityApi.changePassword(payload).subscribe(() => {
      this.notificationService.showSuccess('Password has been changed');
      this.dialogRef.close();
    });
  }

  /**
   * Checks if passwords don't match for displaying validation message.
   */
  get passwordsMismatch(): boolean {
    return (
      this.passwordForm.hasError('mustMatch') &&
      this.passwordForm.controls.newPassword2.valid === true
    );
  }
}
